/**
 * FeaturedPromotions — Carousel showcasing all actively promoted content.
 * Shows promoted tracks, videos, profiles, and events in a scrollable row.
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { TrackArtwork } from './TrackArtwork';
import {
  TrendingUp, Music, Video, User, Calendar,
  ChevronLeft, ChevronRight, Play, Bot, Headphones,
  MapPin, Ticket, Zap
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_COLORS = {
  track: { border: '#ec4899', bg: 'rgba(236,72,153,0.08)', text: '#ec4899', label: 'TRACK' },
  video: { border: '#f97316', bg: 'rgba(249,115,22,0.08)', text: '#f97316', label: 'VIDEO' },
  profile: { border: '#06b6d4', bg: 'rgba(6,182,212,0.08)', text: '#06b6d4', label: 'PROFILE' },
  event: { border: '#10b981', bg: 'rgba(16,185,129,0.08)', text: '#10b981', label: 'EVENT' },
};

const TYPE_ICONS = { track: Music, video: Video, profile: User, event: Calendar };

export default function FeaturedPromotions() {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/promote/featured`)
      .then(res => setPromotions(res.data.featured_promotions || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const scroll = (dir) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir * 320, behavior: 'smooth' });
    }
  };

  const handleClick = (item) => {
    if (item.promote_type === 'track') navigate(`/track/${item.target_id}`);
    else if (item.promote_type === 'video') navigate(`/videos/${item.target_id}`);
    else if (item.promote_type === 'profile') navigate(`/profile/${item.target_id}`);
    else if (item.promote_type === 'event') navigate(`/events/${item.target_id}`);
  };

  if (loading || promotions.length === 0) return null;

  return (
    <section className="px-6 md:px-8 py-8 border-b border-[#1a1a1a]" data-testid="featured-promotions">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Zap size={20} className="text-pink-400" />
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-pink-400 rounded-full animate-pulse" />
            </div>
            <h2 className="text-lg font-['Chakra_Petch'] text-white uppercase tracking-wider">
              Featured Promotions
            </h2>
            <span className="text-pink-400/60 font-mono text-xs">{promotions.length} active</span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => scroll(-1)}
              className="w-8 h-8 border border-[#1a1a1a] text-[#666] hover:text-white hover:border-pink-500/40 flex items-center justify-center transition-all"
              data-testid="featured-promo-prev"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={() => scroll(1)}
              className="w-8 h-8 border border-[#1a1a1a] text-[#666] hover:text-white hover:border-pink-500/40 flex items-center justify-center transition-all"
              data-testid="featured-promo-next"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>

        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto scrollbar-hide pb-2"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {promotions.map((item, idx) => {
            const config = TYPE_COLORS[item.promote_type] || TYPE_COLORS.track;
            const Icon = TYPE_ICONS[item.promote_type] || Music;

            return (
              <div
                key={`${item.promotion_id}-${idx}`}
                onClick={() => handleClick(item)}
                className="flex-shrink-0 w-[280px] border bg-black cursor-pointer group transition-all hover:shadow-lg"
                style={{ borderColor: `${config.border}30`, background: config.bg }}
                data-testid={`featured-promo-${item.promote_type}-${idx}`}
              >
                {/* Artwork/Thumbnail */}
                <div className="aspect-[16/10] bg-[#111] relative overflow-hidden">
                  {item.promote_type === 'track' && (
                    item.artwork ? (
                      <img src={item.artwork.startsWith('http') ? item.artwork : `${BACKEND_URL}/api/artwork/${item.artwork}`} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Music size={40} className="text-[#333]" />
                      </div>
                    )
                  )}
                  {item.promote_type === 'video' && (
                    item.thumbnail ? (
                      <img src={item.thumbnail.startsWith('http') ? item.thumbnail : `${BACKEND_URL}/api/thumbnail/${item.thumbnail}`} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Video size={40} className="text-[#333]" />
                      </div>
                    )
                  )}
                  {item.promote_type === 'profile' && (
                    <div className="w-full h-full flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${config.border}20, transparent)` }}>
                      {item.profile_picture ? (
                        <img src={`${BACKEND_URL}/api/profile/picture/${item.profile_picture}`} alt={item.title} className="w-20 h-20 object-cover border-2" style={{ borderColor: config.border }} />
                      ) : (
                        <div className="w-20 h-20 flex items-center justify-center text-3xl font-bold border-2" style={{ borderColor: config.border, color: config.text }}>
                          {item.title?.[0]?.toUpperCase()}
                        </div>
                      )}
                    </div>
                  )}
                  {item.promote_type === 'event' && (
                    <div className="w-full h-full flex flex-col items-center justify-center p-4" style={{ background: `linear-gradient(135deg, ${config.border}15, transparent)` }}>
                      <Calendar size={36} style={{ color: config.text }} className="mb-2" />
                      <span className="font-mono text-xs" style={{ color: config.text }}>{item.event_date}</span>
                    </div>
                  )}

                  {/* Promoted badge */}
                  <div
                    className="absolute top-2 left-2 px-2 py-0.5 flex items-center gap-1 text-[9px] font-mono font-bold uppercase"
                    style={{ background: config.border, color: '#000' }}
                  >
                    <TrendingUp size={10} /> PROMOTED
                  </div>

                  {/* Type badge */}
                  <div className="absolute top-2 right-2 px-1.5 py-0.5 bg-black/80 text-[9px] font-mono uppercase" style={{ color: config.text }}>
                    <Icon size={10} className="inline mr-1" />{config.label}
                  </div>
                </div>

                {/* Info */}
                <div className="p-3">
                  <h3 className="text-white font-['Chakra_Petch'] text-sm uppercase truncate group-hover:text-pink-300 transition-colors">
                    {item.title}
                  </h3>
                  {item.artist && (
                    <p className="text-[#888] font-mono text-xs truncate mt-0.5">{item.artist}</p>
                  )}
                  {item.bio && (
                    <p className="text-[#666] font-mono text-xs line-clamp-1 mt-0.5">{item.bio}</p>
                  )}
                  {item.venue && (
                    <p className="text-[#666] font-mono text-xs truncate mt-0.5 flex items-center gap-1">
                      <MapPin size={10} /> {item.venue}
                    </p>
                  )}
                  <div className="flex items-center justify-between mt-2 text-[#555] font-mono text-[10px]">
                    <span>by {item.promoter}</span>
                    {item.promote_type === 'track' && (
                      <span className="flex items-center gap-1">
                        <Play size={10} /> {item.play_count || 0}
                      </span>
                    )}
                    {item.promote_type === 'track' && item.price > 0 && !item.is_free && (
                      <span className="text-[#FFDA33] font-bold">${item.price}</span>
                    )}
                    {item.promote_type === 'track' && item.is_free && (
                      <span className="text-[#00ff88]">FREE</span>
                    )}
                    {item.promote_type === 'event' && item.ticket_price && (
                      <span className="flex items-center gap-1">
                        <Ticket size={10} /> ${item.ticket_price}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
